import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import type ButtonTooltipType from '@/components/ui/button-tooltip';
import { cn } from '@/lib/utils';
import styles from './button.module.css';

const VARIANT_CLASS = {
  default: styles.variantDefault,
  destructive: styles.variantDestructive,
  outline: styles.variantOutline,
  secondary: styles.variantSecondary,
  /** Icon-only buttons (aria-label, no visible text): no border, hover tint, yellow focus ring.
   *  Buttons with a text label use `secondary` or `default` instead. */
  ghost: styles.variantGhost,
  link: styles.variantLink,
} as const;

const SIZE_CLASS = {
  /** 56px: marketing hero calls to action, where the button is the focal point */
  xl: styles.sizeXl,
  /** 44px at every width: primary actions stay on the touch target, including on a phone */
  lg: styles.sizeLg,
  /** Matches Input / NativeSelect: 44px below lg, 40px at lg+ */
  default: styles.sizeDefault,
  /** 32px: dense chrome (table pagination, segmented controls) */
  sm: styles.sizeSm,
  /** Square; aligns with default buttons and inputs. */
  icon: styles.sizeIcon,
  /** 28px square. Above the 24px floor. */
  'icon-sm': styles.sizeIconSm,
} as const;

export type ButtonVariant = keyof typeof VARIANT_CLASS;
export type ButtonSize = keyof typeof SIZE_CLASS;

/**
 * Button class string. Also used by links and Radix parts that must look like a button
 * (`<Link className={buttonVariants({ variant: 'outline' })}>`), which is why variants are
 * modifier classes and not data attributes: the caller only gets a className.
 */
function buttonVariants({
  variant,
  size,
  className,
}: { variant?: ButtonVariant | null; size?: ButtonSize | null; className?: string } = {}): string {
  return cn(styles.root, VARIANT_CLASS[variant ?? 'default'], SIZE_CLASS[size ?? 'default'], className);
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant | null;
  size?: ButtonSize | null;
  asChild?: boolean;
  /** Shown on hover and focus. Icon buttons use the accessible name when this is omitted. Pass false to suppress. */
  tooltip?: string | false;
}

const ICON_TOOLTIP_HOLD_MS = 500;

// The tooltip implementation (Radix Tooltip + floating-ui) is its own chunk, so pages without
// icon buttons never download it (CX-397). The app shell preloads it, so by the time an app
// screen renders it is a plain synchronous component and nothing remounts. Until it arrives
// (marketing pages) the bare button is shown; the accessible name never depends on the tooltip.
type ButtonTooltipComponent = typeof ButtonTooltipType;
let loadedTooltip: ButtonTooltipComponent | null = null;
let tooltipLoading: Promise<void> | null = null;
const tooltipListeners = new Set<() => void>();

export function preloadButtonTooltip(): Promise<void> {
  tooltipLoading ??= import('@/components/ui/button-tooltip')
    .then((m) => {
      loadedTooltip = m.default;
      tooltipListeners.forEach((listener) => listener());
    })
    .catch(() => {
      // A failed chunk leaves buttons without a tooltip; the next render tries again.
      tooltipLoading = null;
    });
  return tooltipLoading;
}

function subscribeTooltip(listener: () => void) {
  tooltipListeners.add(listener);
  return () => {
    tooltipListeners.delete(listener);
  };
}

function useButtonTooltipComponent(wanted: boolean): ButtonTooltipComponent | null {
  const component = React.useSyncExternalStore(subscribeTooltip, () => loadedTooltip);
  React.useEffect(() => {
    if (wanted && !loadedTooltip) void preloadButtonTooltip();
  }, [wanted]);
  return component;
}

function iconButtonTooltipLabel(
  size: ButtonProps['size'],
  tooltip: ButtonProps['tooltip'],
  title: ButtonProps['title'],
  ariaLabel: ButtonProps['aria-label'],
): string | undefined {
  if (tooltip === false) return undefined;
  if (typeof tooltip === 'string' && tooltip.trim()) return tooltip;
  const iconOnly = size === 'icon' || size === 'icon-sm';
  if (!iconOnly) return undefined;
  if (typeof title === 'string' && title.trim()) return title;
  if (typeof ariaLabel === 'string' && ariaLabel.trim()) return ariaLabel;
  return undefined;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, tooltip, title, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const tooltipLabel = iconButtonTooltipLabel(size, tooltip, title, props['aria-label']);
    const TooltipImpl = useButtonTooltipComponent(Boolean(tooltipLabel));
    const [tooltipOpen, setTooltipOpen] = React.useState(false);
    const holdTimer = React.useRef<number | null>(null);
    const holdCompleted = React.useRef(false);
    const suppressClick = React.useRef(false);
    const pointerHovering = React.useRef(false);
    const keyboardFocused = React.useRef(false);
    const blockPointerOpenUntil = React.useRef(0);

    const clearHold = React.useCallback(() => {
      if (holdTimer.current != null) {
        window.clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
    }, []);

    React.useEffect(() => clearHold, [clearHold]);

    React.useEffect(() => {
      if (!tooltipLabel) return;
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab' || event.key.startsWith('Arrow')) {
          keyboardFocused.current = true;
        }
      };
      document.addEventListener('keydown', onKeyDown, true);
      return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [tooltipLabel]);

    function allowTooltipOpen() {
      if (holdCompleted.current) return true;
      if (keyboardFocused.current) return true;
      if (Date.now() < blockPointerOpenUntil.current) return false;
      return pointerHovering.current;
    }

    const {
      onPointerDown,
      onPointerEnter,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      onBlur,
      onClick,
      onContextMenu,
      ...rest
    } = props;

    const control = (
      <Comp
        className={buttonVariants({ variant, size, className })}
        data-icon-button={size === 'icon' || size === 'icon-sm' ? '' : undefined}
        ref={ref}
        title={tooltipLabel ? undefined : title}
        onPointerDown={(event) => {
          onPointerDown?.(event);
          if (!tooltipLabel) return;
          keyboardFocused.current = false;
          if (event.pointerType === 'touch' || event.pointerType === 'pen') {
            blockPointerOpenUntil.current = Date.now() + 800;
            pointerHovering.current = false;
            holdCompleted.current = false;
            clearHold();
            holdTimer.current = window.setTimeout(() => {
              holdCompleted.current = true;
              setTooltipOpen(true);
            }, ICON_TOOLTIP_HOLD_MS);
            return;
          }
          pointerHovering.current = true;
        }}
        onPointerEnter={(event) => {
          onPointerEnter?.(event);
          if (event.pointerType === 'mouse') pointerHovering.current = true;
        }}
        onPointerUp={(event) => {
          onPointerUp?.(event);
          clearHold();
          if (holdCompleted.current) suppressClick.current = true;
          holdCompleted.current = false;
          pointerHovering.current = false;
          setTooltipOpen(false);
        }}
        onPointerCancel={(event) => {
          onPointerCancel?.(event);
          clearHold();
          holdCompleted.current = false;
          pointerHovering.current = false;
          setTooltipOpen(false);
        }}
        onPointerLeave={(event) => {
          onPointerLeave?.(event);
          clearHold();
          pointerHovering.current = false;
        }}
        onBlur={(event) => {
          onBlur?.(event);
          keyboardFocused.current = false;
        }}
        onContextMenu={(event) => {
          onContextMenu?.(event);
          if (holdCompleted.current) event.preventDefault();
        }}
        onClick={(event) => {
          if (suppressClick.current) {
            suppressClick.current = false;
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClick?.(event);
        }}
        {...rest}
      />
    );
    if (!tooltipLabel || !TooltipImpl) return control;
    return (
      <TooltipImpl
        label={tooltipLabel}
        open={tooltipOpen}
        onOpenChange={(next) => {
          if (!next) {
            setTooltipOpen(false);
            return;
          }
          if (allowTooltipOpen()) setTooltipOpen(true);
        }}
      >
        {control}
      </TooltipImpl>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
